/**
 * useJobDetail Hook
 *
 * React Query hook for fetching a single job's details.
 */

import { queryKeys } from '@/lib/query-client';
import { PERMIT_CATEGORY, PERMIT_STATUS } from '@skewb-works/contracts';
import type { AddJobTaskBody, JobDetail } from '@skewb-works/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addJobTask, getJobDetail, removeJobTask, setJobTaskMandatory } from '../api/jobDetailApi';

const PERMIT_POLL_INTERVAL_MS = 5000;

// Poll job detail while a permit state is still resolving, so the card and
// status gates update as soon as CORE's next event lands (WRK-5704).
function shouldPollForPermit(data: JobDetail | undefined): boolean {
  if (!data) return false;

  const category = data.permit_category ?? null;
  if (category === PERMIT_CATEGORY.NONE) return false;

  // Awaiting the category, or awaiting the permit row once the category resolved.
  if (category == null || category === PERMIT_CATEGORY.PENDING) return true;
  const permit = data.permit;
  if (!permit) return true;

  // Permit exists — poll only while a lifecycle action is in flight.
  return (
    permit.status === PERMIT_STATUS.PENDING_START || permit.status === PERMIT_STATUS.PENDING_STOP
  );
}

export function useJobDetail(jobId: number) {
  const query = useQuery({
    queryKey: queryKeys.jobs.detail(String(jobId)),
    queryFn: () => getJobDetail(jobId),
    enabled: jobId > 0,
    refetchInterval: (q) => (shouldPollForPermit(q.state.data) ? PERMIT_POLL_INTERVAL_MS : false),
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useAddJobTask(jobId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AddJobTaskBody) => addJobTask(jobId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.detail(String(jobId)) });
    },
    meta: {
      successTitle: 'Task Added',
      successMessage: 'Job task added successfully',
    },
  });
}

/**
 * Override a task's mandatory state for this job only. Pass `null` to clear the
 * override and fall back to the job category default.
 */
export function useSetJobTaskMandatory(jobId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, isMandatory }: { taskId: number; isMandatory: boolean | null }) =>
      setJobTaskMandatory(jobId, taskId, isMandatory),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.detail(String(jobId)) });
    },
    meta: {
      successTitle: 'Task Updated',
      successMessage: 'Task requirement updated for this job',
    },
  });
}

export function useRemoveJobTask(jobId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskId: number) => removeJobTask(jobId, taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.detail(String(jobId)) });
    },
    meta: {
      successTitle: 'Task Removed',
      successMessage: 'Job task removed successfully',
    },
  });
}
